import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { connect, getLocale, useDispatch, useIntl } from 'umi';
import moment from 'moment';
import PerformanceMetricsTable, { SortBy } from '@/pages/dashboard/components/PerformanceMetricsTable';
import { downloadIngameRevenueTable } from '@/pages/dashboard/services/monetization';
import { formatFilterValues, generateCopyDataForDimensionName, getArrObj } from '@/pages/dashboard/utils/utils';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import pcConsoleIngameTableModel from '@/pages/dashboard/servicesModels/monetization/pcConsoleIngameTableModel';
import { useLocal, useLocalText, useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import { textLocalConfig } from '../../config/constants';
import { useMetricOptions } from '@/pages/dashboard/pages/comparison/_config/hooks';
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from '@/pages/dashboard/components/DownloadFormatModal';
import { useFunctionWithIntl, useMetrics } from './hooks';
import SelectShowValueType from '@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType';
import MetricsSettings from '@/pages/dashboard/components/MetricsSettings';
import { getAggregationObj, getAllRecords } from '@/pages/dashboard/pages/comparison/_config/utils';
import '@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss';
import DimensionAccumulateRow from '@/pages/dashboard/components/DimensionAccumulateRow';
import { FileFormat } from '@/pages/dashboard/components/DownloadFormatModal/types';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';

export const defaultValue = ['sum', 'avg_no_null'];

const OverallPaymentTable = (props: any) => {
	const {
		gameCode,
		rainbow,
		pcConsoleRainbow
		// enName, cnName, isShowDownload, isShowResizeEnd, gameConfig
	} = props;
	const contextObj = useContext(FilterContext);
	const { filterVal, filterLoading } = contextObj || {};
	const { timeType = '', formValue } = filterVal || {};
	// const formValue = useMemo(
	//   () => ({
	//     ...filterVal,
	//     newFilter: true,
	//   }),
	//   [filterVal],
	// );
	const [filterConfirm, setFilterConfirm] = useState(false);
	// console.log(contextObj,props,formValue,formValue);

	const { isEN } = useLocal();
	const localText = useLocalText(textLocalConfig);
	const { setNoticeStatus } = useNotificationOfDownloadTable(localText?.countryTitle);
	const { formatMessage } = useIntl();
	// ShowMoreStatistics 下拉多选 相关
	const [showValue, setShowValue] = useState(defaultValue);

	const metrics = useMetrics(timeType);

	// Accumulated 筛选器 相关
	let selectOptions = useSelectArrObj(formValue);
	selectOptions = selectOptions?.filter((i: { groupKey: string }) => i?.groupKey === 'separated');
	selectOptions.unshift({
		groupKey: 'separated',
		value: 'date',
		text: formatMessage({ id: 'common_by_Date' }),
		disabled: true
	});
	const [accumulated, setAccumulated] = useState<string[]>(['date']);
	// Metrics 筛选器相关
	const { metricsValue, setMetricsValue, allMetricOptions } = useMetricOptions(metrics);

	const metricsObj = getArrObj(metrics);
	// value only 切换
	const [segmentValue, setSelectType] = useState('value_only');
	// 列定义相关
	const [columns, setColumns] = useState<any>([]);
	// 表格请求相关
	const [tableData, setTableData] = useState({});
	// 排序值
	const [sortValue, setSortValue] = useState<SortBy[]>([]);
	// 分页
	const [tablePaging, setTablePaging] = useState({ pageIndex: 1, pageSize: 10 });
	const [total, setTotal] = useState(0);

	const accumulateOldValueRef = useRef(['date']);
	const { DATE_COLUMN, getAccumulateColumns, getMetricColumns } = useFunctionWithIntl(localText, formValue?.timeType);

	const dispatch = useDispatch();
	const churnUsersData = pcConsoleIngameTableModel(
		props,
		formValue,
		dispatch,
		filterLoading,
		accumulateOldValueRef.current,
		tablePaging,
		sortValue,
		segmentValue,
		filterConfirm
	);
	const { componentData, error, loading } = churnUsersData;

	const getColumns = () => {
		setColumns([
			DATE_COLUMN,
			...getAccumulateColumns(accumulated, gameCode, segmentValue),
			...getMetricColumns(metricsValue, metricsObj, rainbow, pcConsoleRainbow, segmentValue)
		]);
	};

	useEffect(() => {
		const { aggregation, metrics_list = [], row_count } = componentData || {};
		const tableData = { metricsList: metrics_list || [] };
		if (aggregation && aggregation.length > 0) {
			tableData.aggregationObj = getAggregationObj(aggregation, localText);
		}
		setTableData(tableData);
		setTotal(row_count);
		getColumns();
	}, [componentData, segmentValue, metricsValue, getLocale()]);

	useEffect(() => {
		if (filterConfirm) {
			setTablePaging({ pageIndex: 1, pageSize: 10 });
			setSortValue([]);
		}
	}, [filterConfirm]);

	const tableCopyData = useMemo(() => {
		const dtd = getAllRecords(tableData, showValue) as any;
		if (!dtd) return [];
		return generateCopyDataForDimensionName(dtd);
	}, [tableData, getLocale()]);

	// 下载
	function handleDownload(isDownloadStatistics: boolean, metricsValue: string[], dataType: string, fileFormat: FileFormat) {
		const downloadPromise = downLoadTable?.(isDownloadStatistics, metricsValue, dataType);
		if (downloadPromise) {
			// 有返回Promise对象时由内部处理下载过程
			// 展示正在下载中通知
			setNoticeStatus('loading');
			// 发起请求下载
			(async () => {
				try {
					const res = await downloadPromise;
					const fileName = `${formatMessage({ id: 'pc_console_revenue_overview_overall_performance' })} ${moment().format(
						'YYYY_MM_DD HH_mm_ss'
					)}`;
					await downloadFileWithFileFormat(
						res,
						fileName,
						{
							onDownloadSuccess() {
								setNoticeStatus('success');
							},
							onDownloadError() {
								setNoticeStatus('error');
							}
						},
						fileFormat
					);
				} catch (e) {
					setNoticeStatus('error');
				}
			})();
		}
	}

	function downLoadTable(isDownloadStatistics: any, metrics: any, dataType: string) {
		const accumulation = {
			by_zone: accumulated?.includes('zone'),
			by_region: accumulated?.includes('region'),
			by_country: accumulated?.includes('country'),
			by_channel: accumulated?.includes('channel'),
			by_system: accumulated?.includes('system'),
			by_language: accumulated?.includes('language')
		};
		let desc = sortValue[0]?.order === 'desc' || !sortValue[0];
		let sorted_by = sortValue?.[0]?.by?.replace(segmentValue, '') || 'date';

		const { rangeTime, ...filters } = formValue?.filterValue || {};
		const params = {
			game_code: gameCode,
			start_date: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
			end_date: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
			period: PERIODTIMEOBJ[timeType],
			filters: formatFilterValues(filters),
			aggregates: isDownloadStatistics ? showValue : [],
			metrics: metrics,
			accumulation,
			desc,
			sorted_by,
			page: tablePaging?.pageIndex,
			page_size: tablePaging?.pageSize,
			show_dod: dataType === 'show_dod',
			show_sdlw: dataType === 'show_sdlw'
		};
		return downloadIngameRevenueTable(params, isEN ? 'en' : 'zh');
	}

	return (
		<div className='dbd-overall-performance'>
			<PerformanceMetricsTable
				title={formatMessage({ id: 'monetization_revenue_Overall_Payment' })}
				timeType={timeType}
				topTip={{
					left: (
						<div className='overall-conext-bottom'>
							<DimensionAccumulateRow
								className='register-table-select margin-r-20'
								value={accumulated}
								options={selectOptions}
								onChange={value => {
									setAccumulated(value);
									if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
										accumulateOldValueRef.current = value;
										// 分页和排序重置
										setFilterConfirm(true);
										setTablePaging({ pageIndex: 1, pageSize: 10 });
										setSortValue([]);
									}
								}}
							/>
							<SelectShowValueType
								timeType={timeType}
								value={segmentValue}
								onChange={value => {
									setSelectType(value);
								}}
								isShowDod
								isShowSdlw
							/>
						</div>
					),
					right: (
						<MetricsSettings
							onConfirm={({ metricValues, statisticsType }: any) => {
								setShowValue(statisticsType);
								setMetricsValue(metricValues);
							}}
							disabledOptions={[{ value: 'date', text: useIntl().formatMessage({ id: 'common_date' }) }]}
							metricOptions={allMetricOptions}
						/>
					)
				}}
				columns={columns}
				records={getAllRecords(tableData, showValue)}
				copyConfig={{
					copyData: tableCopyData
				}}
				selectType={segmentValue}
				loading={loading || filterLoading}
				isError={error}
				// isShowProgressBar
				downloadConfig={{
					onDownload: ({ isDownloadStatistics, metrics: metricsValue, dataType, fileFormat }) => {
						handleDownload(isDownloadStatistics, metricsValue, dataType, fileFormat);
					},
					timeType: timeType,
					metricOptions: allMetricOptions,
					showData: { valueOnly: true, showDod: true, showSdlw: true }
				}}
				sorting={{
					defaultSorts: sortValue,
					onSortChange: sorts => {
						setSortValue(sorts);
						setFilterConfirm(false);
					}
				}}
				paging={{
					pageIndex: tablePaging.pageIndex,
					pageSize: tablePaging.pageSize,
					recordCount: total,
					onPagingChange: (query?: any) => {
						setTablePaging(query);
						setFilterConfirm(false);
					}
				}}
				onResizeEnd={columns => setColumns(columns)}
			/>
		</div>
	);
};

export default connect((state: any) => {
	return {
		gameCode: state.dashboard?.currentGame?.game_code,
		rainbow: state.dashboard?.rainbow,
		pcConsoleRainbow: state.dashboard?.pcConsoleRainbow,
		groupByOption: state.dashboard?.groupByOption,
		filterSelectorData: state.dashboard?.filterSelectorData,
		gameConfig: state.dashboardSetting?.gameConfig
	};
})(OverallPaymentTable);
